import { useMemo, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  addMembersToCloudIdentityGroup,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import GeneralDialog from '../../../../shared/general-dialog';
import { GridColDef } from '@mui/x-data-grid-pro';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { emailRegex } from '../../../../utils/regular-expressions';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import GroupMembershipExpirationChip from './group-membership-expiration-chip';
import { cloudidentity_v1 } from '@googleapis/cloudidentity';
import { getHighestRole } from './group-ci-membership-grid';
import { ApiException, UserSettings } from '../../../../../types';
import { Messages } from '../../../../utils/message-constants';
import axios from 'axios';
import { useUserSettings } from '../../../../utils/use-user-settings';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Groups from '@mui/icons-material/Groups';
import Person from '@mui/icons-material/Person';
import PersonOff from '@mui/icons-material/PersonOff';

interface BulkAddGroupCIMembersDialogProps {
  open: boolean;
  handleClose: () => void;
  groupEmail: string;
  changeLogRoles: string[];
  memberList: cloudidentity_v1.Schema$Membership[];
  parentGroupId: string;
  addAllMembersDisabled?: boolean;
  directoryRoles: string[];
}

export function handleCIGroupError(error: unknown, groupEmail: string) {
  if (axios.isAxiosError(error)) {
    const errorResponse = error.response?.data as ApiException;
    const message = errorResponse.message ?? '';
    let snackbarMessage: string;

    switch (true) {
      case message.includes('Error(4018)'):
        snackbarMessage = `One or more members could not be added as they do not meet the group's membership restrictions. Please review the security settings and try again.`;
        break;
      case message.includes('Error(4023)'):
        snackbarMessage = `Unable to add external members to group ${groupEmail}.`;
        break;
      case message.includes('Error(4008)'):
        snackbarMessage = `Could not add one or more members. This is a security group, and only other security groups can be added as members.`;
        break;
      case message.includes('Error(2028)'):
        snackbarMessage = `Permission denied for ${groupEmail} (or it may not exist).`;
        break;
      case message.includes('Error(4003)'):
        snackbarMessage = `Member already exists in group ${groupEmail}.`;
        break;
      case message.includes('Condition not met'):
        snackbarMessage = `Could not add one or more members. This may be due to a security label or other restrictive security settings.`;
        break;
      default:
        snackbarMessage = Messages.Errors.Network.Adding(
          `members to ${groupEmail}`
        );
    }
    handleErrors({ error, snackbarMessage });
  } else {
    handleErrors({
      error,
      snackbarMessage: Messages.Errors.Network.Adding(
        `members to ${groupEmail}`
      ),
    });
  }
}

export default function BulkAddGroupCIMembersDialog({
  open,
  handleClose,
  groupEmail,
  changeLogRoles,
  memberList,
  parentGroupId,
  addAllMembersDisabled,
  directoryRoles,
}: BulkAddGroupCIMembersDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { allDomains, primaryDomain } = useUserSettings();
  const { setSnackbar } = useStore();
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
  const [confirmationError, setConfirmationError] = useState(false);
  const [confirmAddAll, setConfirmAddAll] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<
    cloudidentity_v1.Schema$Membership[]
  >([]);
  const userSettings = queryClient.getQueryData<UserSettings>([
    UserKeys.UserSettings,
  ]);
  const customerId = userSettings?.subscription?.customerId;

  // Check if user has permission to add all current and future users within domain
  const canAddAllCurrentAndFutureUsers = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.AddAllCurrentAndFutureUsersWithinDomain.value
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const membersToAdd = selectedMembers.map((member) => {
        // the 'type' property is used for UI logic and should not be sent to the api
        const { type: _type, ...apiMember } = member;
        return apiMember;
      });
      return addMembersToCloudIdentityGroup(
        membersToAdd,
        parentGroupId,
        groupEmail,
        changeLogMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: selectedMembers.length > 1 ? 'info' : 'success',
        message:
          selectedMembers.length > 1
            ? 'The task to add these members to group has started.'
            : Messages.Success.Added('Member'),
      });
      // cant do setquery because cloudidentity_v1.Schema$Membership name needs membershipid which hasnt been created yet
      if (selectedMembers.length === 1) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GroupMembersCloudIdentity, groupEmail],
        });
      }
      setSelectedRows([]);
      setSelectedMembers([]);
      setAutoCompleteResponse('');
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => handleCIGroupError(error, groupEmail),
    onSettled: () => {
      createEvent(analytics.directory.group_details.create_group_member, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isCheckboxSelected && !confirmAddAll) {
      setConfirmationError(true);
      return;
    }
    setConfirmationError(false);
    const hasExternalMembers = selectedMembers.some((member) => {
      const email = member.preferredMemberKey?.id ?? '';
      const domain = email.split('@')[1]?.toLowerCase();

      return domain && !allDomains.includes(domain);
    });

    // Check if changelog is required for the specific action
    const needsChangeLog =
      (isCheckboxSelected &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.AddAllCurrentAndFutureUsersWithinDomain.value
        )) ||
      (!isCheckboxSelected &&
        (isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.AddMemberToGroup.value
        ) ||
          (hasExternalMembers &&
            isAbleToSeeUIElement(
              changeLogRoles,
              DirectoryRoles.AddExternalMemberToGroup.value
            ))));

    if (needsChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const memberListEmails = memberList.map(
    (member) => member.preferredMemberKey?.id ?? ''
  );

  function addSelection(email: string, type?: string) {
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
    } else if (email === groupEmail) {
      setErrorMessage('Cannot add the group as a member of itself.');
    } else if (memberListEmails.includes(email)) {
      setErrorMessage('User/Group is already a member of this group.');
    } else if (
      selectedMembers.some(
        (member) => (member.preferredMemberKey?.id ?? '') === email
      )
    ) {
      setErrorMessage('User/Group has already been selected.');
    } else {
      setErrorMessage('');
      setAutoCompleteResponse('');
      const newGroupMember: cloudidentity_v1.Schema$Membership = {
        preferredMemberKey: {
          id: email,
        },
        roles: [
          {
            name: 'MEMBER',
          },
        ],
        type: type ? type.toUpperCase() : 'USER',
      };
      setSelectedMembers([...selectedMembers, newGroupMember]);
    }
  }

  function removeSelection(emails: string[]) {
    const newSelectedMembers = selectedMembers.filter(
      (member) => !emails.includes(member.preferredMemberKey?.id ?? '')
    );
    setSelectedMembers(newSelectedMembers);
    setSelectedRows([]);
  }

  function handleCheckboxChange(checked: boolean) {
    setIsCheckboxSelected(checked);

    if (checked) {
      const wildcardMember: cloudidentity_v1.Schema$Membership = {
        preferredMemberKey: {
          id: '*',
        },
      };

      setSelectedMembers((prev) => {
        if (!prev.some((member) => member.preferredMemberKey?.id === '*')) {
          return [...prev, wildcardMember];
        }
        return prev;
      });
    } else {
      setConfirmAddAll(false);
      setSelectedMembers((prev) =>
        prev.filter((member) => member.preferredMemberKey?.id !== '*')
      );
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        editable: false,
        sortable: false,
        flex: 1,
        display: 'flex',
        valueGetter: (_, row) => row.preferredMemberKey?.id,
        renderCell: (params) => {
          const emailDisplay =
            params.value === '*'
              ? 'All current and future users within domain'
              : params.value;

          return <Typography variant="body2">{emailDisplay}</Typography>;
        },
      },
      {
        field: 'role',
        headerName: 'Role',
        sortable: false,
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
          const member = params.row;
          const isWildcard = member.preferredMemberKey?.id === '*';

          return (
            <TextField
              label="Role"
              select
              value={getHighestRole(member.roles)?.name ?? 'MEMBER'}
              onChange={(event) => {
                const newHighestRole = event.target.value as
                  | 'MEMBER'
                  | 'MANAGER'
                  | 'OWNER';

                setSelectedMembers((prev) =>
                  prev.map((memberToUpdate) => {
                    if (memberToUpdate.preferredMemberKey?.id !== params.id) {
                      return memberToUpdate;
                    }

                    const currentMemberRole =
                      memberToUpdate.roles?.find(
                        (role) => role.name === 'MEMBER'
                      ) ?? null;

                    const newRoles =
                      newHighestRole === 'MEMBER'
                        ? [
                            {
                              name: 'MEMBER',
                              ...(currentMemberRole?.expiryDetail && {
                                expiryDetail: currentMemberRole.expiryDetail,
                              }),
                            },
                          ]
                        : [
                            {
                              name: 'MEMBER',
                              ...(currentMemberRole?.expiryDetail && {
                                expiryDetail: currentMemberRole.expiryDetail,
                              }),
                            },
                            { name: newHighestRole },
                          ];

                    return { ...memberToUpdate, roles: newRoles };
                  })
                );
              }}
              size="small"
              margin="none"
              sx={{ minWidth: 180 }}
              disabled={isWildcard}
              slotProps={{
                input: {
                  id: `member-role-${params.id}`,
                },
                inputLabel: {
                  htmlFor: `member-role-${params.id}`,
                },
              }}
            >
              <MenuItem value="MEMBER">Member</MenuItem>
              {member.type?.toUpperCase() === 'USER' && !isWildcard && (
                <MenuItem value="MANAGER">Manager</MenuItem>
              )}
              {member.type?.toUpperCase() === 'USER' && !isWildcard && (
                <MenuItem value="OWNER">Owner</MenuItem>
              )}
            </TextField>
          );
        },
      },
      {
        field: 'expiration',
        headerName: 'Expiration',
        sortable: false,
        filterable: false,
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
          const member: cloudidentity_v1.Schema$Membership = params.row;
          if (!member) return null;

          const memberEmail = member.preferredMemberKey?.id ?? '';
          const emailDomain = memberEmail.split('@')[1]?.toLowerCase();
          const isExternal =
            emailDomain &&
            !allDomains.includes(emailDomain) &&
            primaryDomain.includes(emailDomain);

          if (member.preferredMemberKey?.id === '*') {
            return <Chip label="Not applicable" size="small" disabled />;
          }

          if (isExternal) {
            return (
              <Chip
                label="Expiry can be set after adding"
                size="small"
                disabled
              />
            );
          }

          return (
            <GroupMembershipExpirationChip
              memberEmail={member.preferredMemberKey?.id ?? ''}
              currentExpirationTime={
                member.roles?.find((role) => role.name === 'MEMBER')
                  ?.expiryDetail?.expireTime || ''
              }
              memberRole={getHighestRole(member.roles)?.name}
              onExpirationChange={(newExpirationTime) => {
                setSelectedMembers((prev) =>
                  prev.map((memberToUpdate) =>
                    memberToUpdate.preferredMemberKey?.id === params.id
                      ? {
                          ...memberToUpdate,
                          roles: memberToUpdate.roles?.map((role) =>
                            role.name === 'MEMBER'
                              ? {
                                  ...role,
                                  expiryDetail: {
                                    expireTime: newExpirationTime,
                                  },
                                }
                              : role
                          ),
                        }
                      : memberToUpdate
                  )
                );
              }}
            />
          );
        },
      },
      {
        field: 'type',
        headerName: 'Type',
        editable: false,
        sortable: true,
        filterable: false,
        display: 'flex',
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" gap={1}>
              {params.row.type === 'GROUP' ? (
                <Tooltip arrow title="Group">
                  <Groups />
                </Tooltip>
              ) : (
                <Tooltip arrow title="User">
                  <Person />
                </Tooltip>
              )}
            </Stack>
          );
          return avatar;
        },
      },
    ],
    [allDomains, primaryDomain]
  );

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        secondaryButtons={
          <SharedIconButton
            variant="delete"
            onClick={() => removeSelection(selectedRows)}
            disabled={selectedRows.length === 0}
            tooltipLabel="Delete"
          />
        }
      />
    );
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        setAutoCompleteResponse('');
        setErrorMessage('');
        setSelectedRows([]);
        setSelectedMembers([]);
        setIsCheckboxSelected(false);
        setConfirmAddAll(false);
        setConfirmationError(false);
        handleClose();
      }}
      title="Add Members"
      maxWidth="md"
      dialogContent={
        <Stack gap={2}>
          <Typography variant="body2" paddingLeft={2}>
            Select any groups or users you'd like added as members to this
            group. Members must comply with the set security settings for the
            group.
          </Typography>
          <Stack paddingLeft={2} alignItems="flex-start">
            <Tooltip
              arrow
              title={
                !canAddAllCurrentAndFutureUsers
                  ? Messages.Errors.Permission.Acting(
                      'Add all current and future users within domain'
                    )
                  : addAllMembersDisabled
                  ? Messages.Info.SecuritySettingsDisabled(groupEmail)
                  : memberList?.some(
                      (member) => member.preferredMemberKey?.id === customerId
                    )
                  ? Messages.Info.AllUsersAlreadyAdded(groupEmail)
                  : ''
              }
            >
              <span>
                <FormControlLabel
                  required
                  control={
                    <Checkbox
                      checked={
                        isCheckboxSelected ||
                        memberList?.some(
                          (member) =>
                            member.preferredMemberKey?.id === customerId
                        )
                      }
                      onChange={(e) => handleCheckboxChange(e.target.checked)}
                      disabled={
                        !canAddAllCurrentAndFutureUsers ||
                        addAllMembersDisabled ||
                        memberList?.some(
                          (member) =>
                            member.preferredMemberKey?.id === customerId
                        )
                      }
                    />
                  }
                  label="Add all current and future users within domain."
                />
              </span>
            </Tooltip>
            <Collapse
              in={
                isCheckboxSelected &&
                canAddAllCurrentAndFutureUsers &&
                !addAllMembersDisabled
              }
            >
              <Alert severity="warning" sx={{ alignItems: 'start', mt: 1 }}>
                This will add every current and future user in your domain to
                this group. Users will be automatically added as they join your
                organization. To continue, confirm by checking the box below.
                <FormControl
                  required
                  error={confirmationError}
                  sx={{ display: 'block', mt: 1, pl: 1.5 }}
                >
                  <FormControlLabel
                    required
                    control={
                      <Checkbox
                        color="warning"
                        checked={confirmAddAll}
                        onChange={(e) => {
                          setConfirmAddAll(e.target.checked);
                          if (e.target.checked) {
                            setConfirmationError(false);
                          }
                        }}
                      />
                    }
                    label="I understand that all current and future users will be added to this group."
                  />
                  {confirmationError && (
                    <FormHelperText sx={{ ml: 0 }}>
                      Confirmation is required to add all users.
                    </FormHelperText>
                  )}
                </FormControl>
              </Alert>
            </Collapse>
          </Stack>
          <Stack
            flexDirection={'row'}
            gap={1}
            paddingLeft={2}
            alignItems="flex-start"
          >
            <Box sx={{ width: 500 }}>
              <SharedAutocomplete
                freeSolo
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteResponse}
                setAutoCompleteValue={setAutoCompleteResponse}
                errors={!!errorMessage}
                helperText={errorMessage || ' '}
                onSelection={(value, type) => {
                  if (!value) return;
                  addSelection(value, type);
                }}
              />
            </Box>
          </Stack>
          <Box sx={{ height: 500, pl: 2, pr: 2 }}>
            <SharedDataGridClient
              slots={{
                toolbar: CustomToolbar,
                ...createGridOverlays(
                  'Search Above',
                  <PersonOff />,
                  'Try adding user or group.'
                ),
              }}
              pageSizeOptions={[100]}
              getRowId={(row) => row.preferredMemberKey?.id ?? ''}
              checkboxSelection
              disableColumnMenu
              keepNonExistentRowsSelected
              columns={columns}
              rows={selectedMembers}
              setSelectedRows={setSelectedRows}
              selectedRows={selectedRows}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: selectedMembers.length === 0 && !isCheckboxSelected,
        title: 'Add',
      }}
    />
  );
}
